import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  CalendarClock,
  MessageSquareText,
  RefreshCw,
  ShieldAlert,
  Upload,
  Users
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { Sidebar } from '../components/Sidebar'
import {
  buildLineOperations,
  type LineOperationsRow
} from '../lib/lineOperations'
import { hasInternalApi, internalApiGet } from '../lib/internalApi'
import { supabase } from '../lib/supabase'
import { useLivers } from '../lib/useLivers'
import type { SavedLineImport } from '../lib/useLineImports'

type LineImportsResponse = {
  line_imports: SavedLineImport[]
}

export function LinePage() {
  const livers = useLivers()
  const imports = useQuery<SavedLineImport[]>({
    queryKey: ['line-imports', 'all'],
    queryFn: async () => {
      if (hasInternalApi()) {
        const data = await internalApiGet<LineImportsResponse>('/api/line-imports?limit=1000')
        return data.line_imports
      }

      const { data, error } = await supabase
        .from('line_imports')
        .select('id,liver_id,uploaded_by,file_name,source_hash,message_count,active_days,date_start,date_end,primary_manager_name,analysis,handoff_note,created_at,updated_at')
        .order('created_at', { ascending: false })
        .limit(1000)
      if (error) throw error
      return (data ?? []) as SavedLineImport[]
    }
  })
  const dashboard = buildLineOperations(imports.data ?? [], livers.data ?? [], new Date())
  const loading = imports.isLoading || livers.isLoading
  const error = imports.error ?? livers.error

  return (
    <div className="flex min-h-full">
      <Sidebar />
      <main className="min-w-0 flex-1 bg-gray-50 px-4 py-6 pt-16 sm:px-6 md:pt-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">LINE</h1>
              <p className="mt-1 text-sm leading-6 text-gray-500">
                保存済みLINE履歴から、担当候補・引き継ぎ・注意シグナルを横断確認します。
              </p>
            </div>
            {loading && <p className="text-xs text-gray-400">集計中...</p>}
            {error && (
              <p className="text-xs text-red-600">
                LINE履歴の取得に失敗: {error instanceof Error ? error.message : String(error)}
              </p>
            )}
          </header>

          <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <LineMetric
              icon={<MessageSquareText className="h-4 w-4" />}
              label="保存済み履歴"
              value={`${dashboard.totalImportCount}件`}
              note={`${dashboard.totalMessageCount.toLocaleString()} messages`}
              tone="sky"
            />
            <LineMetric
              icon={<Users className="h-4 w-4" />}
              label="履歴ありライバー"
              value={`${dashboard.importedLiverCount}/${dashboard.liverCount}`}
              note={`未アップロード ${dashboard.missingImportCount}名`}
              tone="emerald"
            />
            <LineMetric
              icon={<ShieldAlert className="h-4 w-4" />}
              label="注意シグナル"
              value={`${dashboard.riskImportCount}件`}
              note="税務/返信遅延/権利確認など"
              tone={dashboard.riskImportCount > 0 ? 'rose' : 'slate'}
            />
            <LineMetric
              icon={<RefreshCw className="h-4 w-4" />}
              label="引き継ぎ候補"
              value={`${dashboard.handoffImportCount}件`}
              note={`古い履歴 ${dashboard.staleImportCount}件`}
              tone={dashboard.handoffImportCount > 0 ? 'amber' : 'slate'}
            />
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white">
            <div className="flex flex-col gap-2 border-b border-gray-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">LINE履歴ステータス</h2>
                <p className="mt-1 text-xs text-gray-500">
                  ライバー詳細から txt を読み込むと、この一覧に保存状況と分析結果が反映されます。
                </p>
              </div>
              <span className="w-fit rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-500">
                {dashboard.rows.length}名
              </span>
            </div>
            {loading ? (
              <p className="px-4 py-8 text-sm text-gray-400">読み込み中...</p>
            ) : dashboard.rows.length > 0 ? (
              <ul className="divide-y divide-gray-100">
                {dashboard.rows.map((row) => (
                  <LineRow key={row.liverId} row={row} />
                ))}
              </ul>
            ) : (
              <p className="px-4 py-8 text-sm text-gray-400">対象ライバーがまだありません。</p>
            )}
          </section>
        </div>
      </main>
    </div>
  )
}

type MetricTone = 'sky' | 'emerald' | 'rose' | 'amber' | 'slate'

function LineMetric({
  icon,
  label,
  value,
  note,
  tone
}: {
  icon: React.ReactNode
  label: string
  value: string
  note: string
  tone: MetricTone
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium text-gray-500">{label}</p>
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${TONE_CLASS[tone]}`}>
          {icon}
        </span>
      </div>
      <p className="mt-4 text-2xl font-semibold tabular-nums text-gray-900">{value}</p>
      <p className="mt-1 truncate text-xs text-gray-500">{note}</p>
    </div>
  )
}

function LineRow({ row }: { row: LineOperationsRow }) {
  const to = row.latestImportId ? `/line/imports/${row.latestImportId}` : `/liver/${row.liverId}`

  return (
    <li>
      <Link
        to={to}
        className="grid gap-3 px-4 py-4 transition hover:bg-gray-50 lg:grid-cols-[minmax(0,1.2fr)_minmax(160px,0.7fr)_minmax(160px,0.8fr)_minmax(180px,1fr)]"
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-medium text-gray-900">{row.liverName}</p>
            {row.needsReview && (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700">要確認</span>
            )}
            {row.importCount === 0 && (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-500">未アップロード</span>
            )}
          </div>
          <p className="mt-1 truncate text-xs text-gray-500">{row.fileName ?? 'LINE履歴なし'}</p>
        </div>

        <RowStat
          icon={<Upload className="h-3.5 w-3.5" />}
          label="保存"
          value={row.importCount > 0 ? `${row.importCount}件 / ${row.totalMessageCount}msg` : 'なし'}
        />
        <RowStat
          icon={<Users className="h-3.5 w-3.5" />}
          label="メイン担当候補"
          value={row.primaryManagerName ?? '-'}
        />
        <div className="min-w-0 rounded-lg bg-gray-50 px-3 py-2 lg:bg-transparent lg:px-0 lg:py-0">
          <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
            <CalendarClock className="h-3.5 w-3.5" />
            期間
          </div>
          <p className="mt-0.5 truncate text-sm text-gray-700">
            {row.dateStart && row.dateEnd ? `${row.dateStart}〜${row.dateEnd}` : '-'}
          </p>
          {(row.riskCount > 0 || row.handoffSignalCount > 0 || row.isStale) && (
            <div className="mt-1 flex flex-wrap gap-1">
              {row.riskCount > 0 && (
                <span className="inline-flex items-center gap-1 rounded bg-rose-50 px-1.5 py-0.5 text-[11px] text-rose-700">
                  <AlertTriangle className="h-3 w-3" />
                  注意 {row.riskCount}
                </span>
              )}
              {row.handoffSignalCount > 0 && (
                <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-700">
                  引継ぎ {row.handoffSignalCount}
                </span>
              )}
              {row.isStale && (
                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-500">
                  60日超
                </span>
              )}
            </div>
          )}
          {row.topicLabels.length > 0 && (
            <p className="mt-1 truncate text-[11px] text-gray-400">{row.topicLabels.join(' / ')}</p>
          )}
        </div>
      </Link>
    </li>
  )
}

function RowStat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-lg bg-gray-50 px-3 py-2 lg:bg-transparent lg:px-0 lg:py-0">
      <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
        {icon}
        {label}
      </div>
      <p className="mt-0.5 truncate text-sm text-gray-700">{value}</p>
    </div>
  )
}

const TONE_CLASS: Record<MetricTone, string> = {
  sky: 'bg-sky-50 text-sky-700',
  emerald: 'bg-emerald-50 text-emerald-700',
  rose: 'bg-rose-50 text-rose-700',
  amber: 'bg-amber-50 text-amber-700',
  slate: 'bg-slate-100 text-slate-700'
}
